<template>
    <!--
        One entry of the menu, wherever it is drawn: in a fold, among the favourites, or
        under a settings heading. One component because the three used to be three
        copies of the same fourteen attributes, and the fourteenth — the `data-testid`
        the browser suite navigates by — was on only one of them.

        The star is OUTSIDE the button and laid over its end, not in its trailing slot:
        a button inside a button is not a thing a browser will let you press, and the
        pin has to be pressable without following the link it is pinned to.

        # The test id is the caller's

        A pinned entry is drawn TWICE — once at the top among the favourites and once in
        its own fold — and two elements answering to `nav-item-stock` is a locator that
        resolves to two things, which Playwright refuses outright. So the favourites
        draw their copies as `nav-favourite-item-<route>` and the folds keep
        `nav-item-<route>`; the star beside each takes its own id from whichever it is.

        `legacyTestid` goes on the WRAPPER rather than on the link, because the link
        already carries an id and an element has one. A browser spec that presses the
        wrapper presses the link inside it, which fills it.

        # `data-tabs`

        An entry that stands for a whole TAB GROUP („Nabava“) leads to the first of its
        tabs and is therefore indistinguishable, by its id alone, from an entry leading
        to that one screen. `data-tabs` names the group, so a spec — and anybody reading
        the DOM — can tell "this opens six screens" from "this opens one".

        # The move controls

        A favourite is also DRAGGABLE, and the row carries three buttons instead of one:
        „premakni gor“, „premakni dol“ and the star. The two arrows are not a second way
        of doing the same thing for the sake of it — a drag needs a mouse, and this
        sidebar is read on an iPad at the desk and by people who work the keyboard. They
        are the fallback that makes the feature exist at all on a touch screen, which is
        why they are always there on a coarse pointer and appear on hover or focus
        everywhere else.

        The link inside is `draggable="false"` on such a row. An anchor is draggable by
        itself and would otherwise become the drag source, handing the browser its href
        as the payload and a drag image of the label alone; switched off, the drag starts
        on the row, which is the thing being moved.

        # No star on a tab group

        A group is a job and not a screen, so there is nothing to put in a favourites
        list: pinning „Nabava“ could only pin whichever of its tabs happens to be first,
        which would then appear among the favourites under its OWN name — „Naročilnice“,
        pinned by somebody who pressed a star beside the word „Nabava“ — and the star
        would light up whenever that one screen was pinned from the palette. The tabs
        are pinned one at a time from the palette instead, which is somebody saying
        which screen they meant.
    -->
    <div
        class="group relative"
        :class="[
            dragging ? 'opacity-50' : '',
            reorder ? 'cursor-grab' : '',
        ]"
        :data-testid="legacyTestid"
    >
        <!-- Where the row being dragged would land. A line in the gap and not a
             highlight on the row under the pointer: the question a drag asks is
             „between which two?“, and a lit-up row answers „instead of this one“. -->
        <span
            v-if="dropEdge"
            class="pointer-events-none absolute inset-x-2 h-0.5 rounded-full bg-primary"
            :class="dropEdge === 'before' ? 'top-0' : 'bottom-0'"
            data-testid="nav-favourite-drop-marker"
        />

        <UButton
            v-bind="target"
            :data-testid="testid"
            :draggable="reorder ? false : undefined"
            :data-tabs="item.tabs"
            :label="collapsed ? undefined : item.label"
            :title="item.label"
            :icon="item.icon"
            :trailing-icon="!collapsed && item.external ? 'i-lucide-external-link' : undefined"
            :color="active ? 'primary' : 'neutral'"
            :variant="active ? 'subtle' : 'ghost'"
            size="lg"
            class="w-full"
            :class="[
                collapsed ? 'justify-center' : 'justify-start',
                collapsed ? '' : indent ? 'ps-6' : '',
                collapsed || item.external ? '' : reorder ? 'pe-24' : 'pe-9',
            ]"
            @click="closeMobileMenu"
        >
            <!-- What is waiting behind the entry, where an entry has a number to show.
                 Nothing is drawn for zero: a badge saying „0“ is a thing to read every
                 morning that never means anything. -->
            <template v-if="!collapsed && item.badge" #trailing>
                <UBadge color="warning" variant="subtle" size="sm">{{ item.badge }}</UBadge>
            </template>
        </UButton>

        <!-- Discreet on purpose: 200 px of sidebar is not the place for three buttons
             standing to attention beside every favourite. They come up with the row
             under the pointer, and they are there from the start where there is no
             pointer to hover with. `disabled` rather than hidden at the ends of the
             list, so the pair does not move about as the row does. -->
        <template v-if="reorder && !collapsed">
            <button
                v-for="control in moveControls"
                :key="control.by"
                type="button"
                class="absolute top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-dimmed opacity-0 hover:text-primary focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-30 group-hover:opacity-100 pointer-coarse:opacity-100"
                :class="control.by < 0 ? 'end-15' : 'end-8'"
                :disabled="!control.enabled"
                :title="$t(control.labelKey)"
                :aria-label="`${item.label}: ${$t(control.labelKey)}`"
                :data-testid="`${testid}-${control.by < 0 ? 'up' : 'down'}`"
                @click="emit('move', control.by)"
            >
                <UIcon :name="control.icon" class="size-4" />
            </button>
        </template>

        <button
            v-if="!collapsed && !item.external && !item.groupEntry"
            type="button"
            class="absolute end-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-dimmed opacity-0 hover:text-primary focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-primary group-hover:opacity-100"
            :class="pinned ? 'opacity-100 text-primary' : ''"
            :title="pinned ? $t('nav.favourite_remove') : $t('nav.favourite_add')"
            :aria-label="pinned ? $t('nav.favourite_remove') : $t('nav.favourite_add')"
            :aria-pressed="pinned"
            :data-testid="`${testid}-star`"
            @click="toggleFavourite(item.name)"
        >
            <!-- One icon for both states, told apart by weight and colour. A crossed
                 out star for "not pinned" reads as "you may not", which is the wrong
                 sentence for a control that is offering. -->
            <UIcon name="i-lucide-star" class="size-4" :class="pinned ? 'fill-current' : ''" />
        </button>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'

    import { useLayout } from '@/layouts/layout'
    import { useNav } from '@/libs/nav'
    import type { INavItem } from '@/types/Nav'

    const props = withDefaults(defineProps<{
        item:       INavItem
        collapsed?: boolean
        /** Indented, for an entry that hangs under a fold's heading. */
        indent?:    boolean
        /** The copy among the favourites, which may not answer to the fold's id. */
        favourite?: boolean
        /** Whether this row can be moved: the arrows, and the grab cursor. */
        reorder?:   boolean
        /** Whether there is a row above / below to swap with. */
        canMoveUp?:   boolean
        canMoveDown?: boolean
        /**
         * Which edge of this row a drop would land on, or '' for none. Only ever set on
         * one row at a time — the list decides which; see Sidebar.vue.
         */
        dropEdge?:  '' | 'before' | 'after'
        /** Whether THIS row is the one being dragged. */
        dragging?:  boolean
        /** An extra handle for a browser spec written before the menu was regrouped. */
        legacyTestid?: string
    }>(), {
        collapsed:    false,
        indent:       false,
        favourite:    false,
        reorder:      false,
        canMoveUp:    false,
        canMoveDown:  false,
        dropEdge:     '',
        dragging:     false,
        legacyTestid: undefined,
    })

    const emit = defineEmits<{
        /** One step up (-1) or one step down (+1). The list works out what that means. */
        move: [by: -1 | 1]
    }>()

    const { closeMobileMenu } = useLayout()

    // The shared menu — one filtered manifest for the whole application, not one per
    // entry. See the note at the top of `libs/nav.ts`.
    const { isEntryActive, isFavourite, toggleFavourite } = useNav()

    const testid = computed((): string =>
        `${props.favourite ? 'nav-favourite-item' : 'nav-item'}-${props.item.name}`)

    // The ENTRY and not its name: the derived entry of a tab group borrows its first
    // member's route, so the two are the same string and answer differently.
    const active = computed((): boolean => isEntryActive(props.item))

    const pinned = computed((): boolean => isFavourite(props.item.name))

    /**
     * The two arrows as data, so the template draws them once rather than twice.
     *
     * Two buttons that differ in a sign, an icon, a word and a position were two
     * near-identical eleven-attribute blocks, and the kind of pair where a fix lands on
     * one of them.
     */
    const moveControls = computed(() => [
        { by: -1 as const, icon: 'i-lucide-chevron-up', labelKey: 'nav.favourite_move_up', enabled: props.canMoveUp },
        { by: 1 as const, icon: 'i-lucide-chevron-down', labelKey: 'nav.favourite_move_down', enabled: props.canMoveDown },
    ])

    /**
     * Where the entry leads.
     *
     * An ORDINARY LINK for the door out of this portal, and that is the whole point of
     * it. It used to fetch a handoff and then window.open the answer, which every
     * browser blocks: a window opened after an await is no longer attributable to the
     * click that opened it. The server mints the token and redirects
     * (HTTP_API/Marketing.go), so the navigation IS the click.
     */
    const target = computed(() => props.item.external
        ? { to: props.item.href, target: '_blank', rel: 'noopener', external: true }
        : { to: { name: props.item.name } })
</script>
